import * as React from 'react';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { buildInitialAddressFromCustomerAddress } from '@klareospace/pharos-lib-front-utils';
import type { AddressInputInitialAddress } from '@klareospace/pharos-lib-front-utils';
import type { Customer, SiteWithSpaceName } from '@klareospace/pharos-lib-types';
import type { DateRangeValue } from '@klareospace/pharos-lib-web-utils';

import { AddressDialog, buildAddressEntryFromCustomerAddress, buildEmptyAddressEntry } from '&/features/customers';
import type { AddressBookEntryInput } from '&/features/customers';

import type { OrderCreationFormInput } from '../../schemas/order-creation.schema';
import { buildDefaultPickupTimeSlotValue, resolveDeliveryContact } from '../../utils';
import { DeliveryBlock } from './delivery-block';
import { PickupBlock } from './pickup-block';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RouteInformationSectionProps {
  /** Whether the current user can list sites — drives the Pickup block's permission-denied message. */
  canListSites: boolean;
  /** Whether `onSyncAddressBook`'s PATCH is in flight — disables the address dialog's Save button. */
  isSyncingAddressBook?: boolean;
  /** PATCHes the selected customer's address book — owned at the drawer's lifetime, see `useOrderCreationForm`. */
  onSyncAddressBook: (entry: AddressBookEntryInput) => void;
  /** Sites scoped to the current user. */
  sites: SiteWithSpaceName[];
}

/** Which address the dialog is currently open for — `null` when the dialog is closed. Mirrors customers' `AddressBookSection`. */
type AddressDialogState = { mode: 'add' } | { index: number; mode: 'edit' } | null;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Seeds the address dialog's search/map state from the stored address at the dialog's index — undefined in add mode. */
function resolveDialogInitialAddress(
  dialogState: AddressDialogState,
  customer: Customer | null,
): AddressInputInitialAddress | undefined {
  if (!dialogState || dialogState.mode === 'add' || !customer) return undefined;
  return buildInitialAddressFromCustomerAddress(customer.addresses[dialogState.index]);
}

/**
 * The entry the dialog should be seeded with. Editing the currently selected address reuses the
 * live form entry (`deliveryAddress`) rather than rebuilding it from the stored address — a user
 * without `customers:manage` never gets their edit PATCHed back, so the stored address would
 * otherwise silently discard it. Editing a different row (or add mode) has no form state to reuse,
 * so it builds fresh from the stored address, blank in add mode.
 */
function resolveDialogEntry(
  dialogState: AddressDialogState,
  deliveryAddress: AddressBookEntryInput | null,
  customer: Customer | null,
): AddressBookEntryInput {
  if (!customer || dialogState?.mode !== 'edit') return buildEmptyAddressEntry();
  if (deliveryAddress && dialogState.index === deliveryAddress.sourceIndex) return deliveryAddress;
  return buildAddressEntryFromCustomerAddress(customer.addresses[dialogState.index], dialogState.index, customer);
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * "Route information" section body — Pick up (scoped-site select) and Delivery (customer address,
 * pre-filled and editable via the shared customers `AddressDialog`) blocks.
 */
export function RouteInformationSection({
  canListSites,
  isSyncingAddressBook = false,
  onSyncAddressBook,
  sites,
}: RouteInformationSectionProps): React.JSX.Element {
  const { control, formState, setValue } = useFormContext<OrderCreationFormInput>();
  const incompleteMessage = formState.errors.routeInformation?.message;

  const selectedSite = useWatch({ control, name: 'routeInformation.selectedSite' });
  const pickupDurationTime = useWatch({ control, name: 'routeInformation.pickupDurationTime' });
  const pickupTimeSlot = useWatch({ control, name: 'routeInformation.pickupTimeSlot' });
  const deliveryAddress = useWatch({ control, name: 'routeInformation.deliveryAddress' });
  const deliveryDurationTime = useWatch({ control, name: 'routeInformation.deliveryDurationTime' });
  const selectedCustomer = useWatch({ control, name: 'customer.selectedCustomer' });

  const [isSiteEditing, setIsSiteEditing] = useState(false);
  const [isAddressEditing, setIsAddressEditing] = useState(false);
  const [dialogState, setDialogState] = useState<AddressDialogState>(null);

  // Closes the picker whenever the selected customer changes (a new pick, or cleared) — a stale
  // picker open on a different customer's addresses would be confusing.
  const selectedCustomerId = selectedCustomer?.id ?? null;
  useEffect(() => {
    setIsAddressEditing(false);
  }, [selectedCustomerId]);

  function handleSelectSite(siteId: string): void {
    const site = sites.find((candidate) => candidate.id === siteId);
    if (!site) return;
    setValue('routeInformation.selectedSite', site, { shouldValidate: true });
    setIsSiteEditing(false);
  }

  function handleToggleTimeslot(enabled: boolean): void {
    const value = enabled
      ? (pickupTimeSlot.value ?? buildDefaultPickupTimeSlotValue(selectedSite))
      : pickupTimeSlot.value;
    setValue('routeInformation.pickupTimeSlot', { enabled, value }, { shouldValidate: true });
  }

  // A customer with no saved address keeps today's direct add-dialog path; otherwise the picker
  // opens (even for exactly one saved address).
  function handleStartEditingAddress(): void {
    if (!selectedCustomer || selectedCustomer.addresses.length === 0) {
      setDialogState({ mode: 'add' });
      return;
    }
    setIsAddressEditing(true);
  }

  function handleSelectAddress(index: number): void {
    if (!selectedCustomer || index === deliveryAddress?.sourceIndex) {
      setIsAddressEditing(false);
      return;
    }
    setValue(
      'routeInformation.deliveryAddress',
      buildAddressEntryFromCustomerAddress(selectedCustomer.addresses[index], index, selectedCustomer),
      { shouldValidate: true },
    );
    setIsAddressEditing(false);
  }

  function handleEditAddressEntry(index: number): void {
    setIsAddressEditing(false);
    setDialogState({ index, mode: 'edit' });
  }

  function handleAddNewAddress(): void {
    setIsAddressEditing(false);
    setDialogState({ mode: 'add' });
  }

  function handleAddressDialogSave(entry: AddressBookEntryInput): void {
    setValue('routeInformation.deliveryAddress', entry, { shouldValidate: true });
    setDialogState(null);
    onSyncAddressBook(entry);
  }

  const deliveryContact = deliveryAddress ? resolveDeliveryContact(deliveryAddress, selectedCustomer) : null;

  return (
    <div className="space-y-6">
      {incompleteMessage && <p className="text-sm text-destructive">{incompleteMessage}</p>}

      <PickupBlock
        canListSites={canListSites}
        isSiteEditing={isSiteEditing}
        onCancelEditingSite={(): void => setIsSiteEditing(false)}
        onSelectSite={handleSelectSite}
        onServiceTimeChange={(value): void =>
          setValue('routeInformation.pickupDurationTime', value, { shouldValidate: true })
        }
        onStartEditingSite={(): void => setIsSiteEditing(true)}
        onTimeslotValueChange={(value: DateRangeValue): void =>
          setValue('routeInformation.pickupTimeSlot.value', value, { shouldValidate: true })
        }
        onToggleTimeslot={handleToggleTimeslot}
        pickupDurationTime={pickupDurationTime}
        pickupTimeSlot={pickupTimeSlot}
        selectedSite={selectedSite}
        sites={sites}
      />

      <DeliveryBlock
        customer={selectedCustomer}
        deliveryAddress={deliveryAddress}
        deliveryContact={deliveryContact}
        deliveryDurationTime={deliveryDurationTime}
        isAddressEditing={isAddressEditing}
        onAddNewAddress={handleAddNewAddress}
        onCancelEditingAddress={(): void => setIsAddressEditing(false)}
        onEditAddressEntry={handleEditAddressEntry}
        onSelectAddress={handleSelectAddress}
        onServiceTimeChange={(value): void =>
          setValue('routeInformation.deliveryDurationTime', value, { shouldValidate: true })
        }
        onStartEditingAddress={handleStartEditingAddress}
      />

      {dialogState && (
        <AddressDialog
          entry={resolveDialogEntry(dialogState, deliveryAddress, selectedCustomer)}
          initialAddress={resolveDialogInitialAddress(dialogState, selectedCustomer)}
          isSaving={isSyncingAddressBook}
          mode={dialogState.mode}
          onClose={(): void => setDialogState(null)}
          onSave={handleAddressDialogSave}
          open
        />
      )}
    </div>
  );
}
